/**
 * BattleLobby
 *
 * The Fun Challenges lobby route's body. Visual language matches the
 * battle screen + result modal that we already ported from NSCL:
 *   - Cream page background, white cards with soft shadows.
 *   - Square Trainer tile = left-side teal icon column + title + description.
 *   - Settings preview = inline chip row, taps to open the SlideModal.
 *   - "Send Challenge" CTA = NSCL yellow button with the 4 px bottom-lip.
 *
 * Tapping the CTA calls `useFunChallengesBattleSocket.sendChallenge`,
 * which optimistically transitions the FSM to `'challenging'`. The
 * `FunChallengesGlobalListener` then renders the "Waiting for opponent…"
 * SlideModal until the server emits `fun_room_invite_*` or
 * `fun_session_start`.
 */

import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import {useRoute} from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import SlideModal from '@/Atoms/SlideModal';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, vs} from '@/Utils/Dimensions';

import ChallengeUserComponent from '@/Screens/PlayChess/components/ChallengeUserComponent';

import useFunChallengesUserSearch from '@/Hooks/FunChallengesUserSearch/useFunChallengesUserSearch';
import useFunChallengesBattleSocket from '@/Hooks/FunChallengesSocket/useFunChallengesBattleSocket';
import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import useGamePlayState from '@/Store/GamePlayStore';

import {BattleType, type ChallengeSettings as Settings} from '@/Screens/FunChallenges/types';
import ChallengeSettings, {
  DEFAULT_CHALLENGE_SETTINGS,
  DEFAULT_PTB_CHALLENGE_SETTINGS,
  DEFAULT_PDB_CHALLENGE_SETTINGS,
} from './ChallengeSettings';
import PuzzleRoyaleLobbyPanel from '@/Screens/FunChallenges/PuzzleRoyaleLobby';
import SoloPuzzleRoyalePanel from '@/Screens/FunChallenges/SoloPuzzleRoyale';
import HostTournamentPanel from '@/Screens/FunChallenges/components/HostTournamentPanel';
import {TIME_CHIPS} from '../royaleConstants';
import useLoggedInUserStore from '@/Store/loggedInUserstore';

// Side-effect import — registers all adapters in the registry.
import '@/Screens/FunChallenges/adapters';

// NSCL design tokens (kept in sync with ScorePanel / BattleResult).
const BG_PAGE = '#FAF6EB';
const BG_PAGE_SOFT = '#F4EEDB';
const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const TEAL_TINT = 'rgba(61, 171, 158, 0.12)';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const BORDER = 'rgba(38, 35, 34, 0.10)';
const BORDER_STRONG = 'rgba(38, 35, 34, 0.18)';
const TEXT_DARK = '#262322';
const TEXT_MID = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const YELLOW = '#FACF47';
const YELLOW_DARK = '#A9873B';
const ORANGE_TINT = 'rgba(225, 120, 70, 0.12)';
const PURPLE_TINT = 'rgba(147, 122, 219, 0.14)';
const BLUE_TINT = 'rgba(103, 179, 225, 0.16)';

interface BattleTypeTileSpec {
  battleType: BattleType;
  title: string;
  description: string;
  iconRequire: any;
  /** Per-tile icon background tint — keeps the three options visually distinct. */
  iconBg: string;
}

// Duel = the three 1v1 modes (existing send-challenge flow).
const DUEL_TILE_SPECS: BattleTypeTileSpec[] = [
  {
    battleType: BattleType.SQUARE_TRAINER,
    title: 'Square Trainer Challenge',
    description:
      'Race your friend to find the most squares in the time limit.',
    iconRequire: require('@/Assets/Images/Svgs/squareTrainerChallenge.svg'),
    iconBg: TEAL_TINT,
  },
  {
    battleType: BattleType.POSITION_TRAINER,
    title: 'Puzzle Duel',
    description:
      'Race your friend to solve real-game puzzles under the clock.',
    // Reuse the ST icon until a PTB-specific asset lands — keeps the tile
    // visible without blocking on art. Swap to a dedicated SVG when ready.
    iconRequire: require('@/Assets/Images/Svgs/postionTrainerBattle.svg'),
    iconBg: ORANGE_TINT,
  },
  {
    battleType: BattleType.PUZZLE_DASH,
    title: 'Puzzle Dash',
    description:
      'Race your friend to solve a chain of puzzles first.',
    // Same placeholder icon until a PDB-specific asset lands.
    iconRequire: require('@/Assets/Images/Svgs/puzzleDashChallenge.svg'),
    iconBg: PURPLE_TINT,
  },
];

// Multiplayer modes. Today just Puzzle Royale, but the Multiplayer tab shows a
// selectable tile list (like Duel's three tiles) so more modes can be added
// later. Picking Puzzle Royale reveals its inline create/join/spectate panel
// (PuzzleRoyaleLobbyPanel) — no separate route, no opponent picker.
const MULTIPLAYER_TILE_SPECS: BattleTypeTileSpec[] = [
  {
    battleType: BattleType.PUZZLE_ROYALE,
    title: 'Puzzle Royale',
    description:
      'Race up to 10 players to solve the most puzzles before time runs out.',
    iconRequire: require('@/Assets/Images/Svgs/puzzleRoyaleChallenge.svg'),
    iconBg: BLUE_TINT,
  },
];

// Solo modes. Today just Solo Puzzle Royale — a 1-player race against the clock
// on a ramping-difficulty ladder. Same selectable-tile pattern (room for more
// solo modes later). Picking it reveals SoloPuzzleRoyalePanel (settings-only +
// Start game — no lobby, no opponent, no share code).
const SOLO_TILE_SPECS: BattleTypeTileSpec[] = [
  {
    battleType: BattleType.PUZZLE_ROYALE,
    title: 'Solo Puzzle Royale',
    description:
      'Solve as many puzzles as you can before time runs out — they get harder as you go.',
    iconRequire: require('@/Assets/Images/Svgs/puzzleRoyaleChallenge.svg'),
    iconBg: BLUE_TINT,
  },
];

type LobbyMode = 'duel' | 'multiplayer' | 'solo' | 'host';

interface ChallengeTileProps {
  spec: BattleTypeTileSpec;
  selected: boolean;
  onPress: () => void;
}

function ChallengeTile({spec, selected, onPress}: ChallengeTileProps) {
  const scaleVal = useRef(new Animated.Value(1)).current;
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (selected) {
      Animated.sequence([
        Animated.timing(scaleVal, {
          toValue: 1.2,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(scaleVal, {
          toValue: 1.0,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.timing(scaleVal, {
        toValue: 1.0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [selected, scaleVal]);

  return (
    <Pressable
      onPress={onPress}
      style={({pressed}) => [
        styles.tile,
        selected && styles.tileSelected,
        pressed && !selected && styles.tilePressed,
      ]}>
      {selected && <View style={styles.tileAccentBar} />}
      <View
        style={[
          styles.tileIconWrap,
          {backgroundColor: selected ? TEAL_TINT : spec.iconBg},
        ]}>
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <Animated.View style={{transform: [{scale: scaleVal}]}}>
          <Image source={spec.iconRequire} style={styles.tileIcon} />
        </Animated.View>
      </View>
      <View style={styles.tileBody}>
        <CCText style={styles.tileTitle}>{spec.title}</CCText>
        <CCText style={styles.tileDesc}>{spec.description}</CCText>
      </View>
      {selected && (
        <View style={styles.tileCheck}>
          <CCText style={styles.tileCheckMark}>✓</CCText>
        </View>
      )}
    </Pressable>
  );
}

export default function BattleLobby() {
  const route = useRoute<any>();
  // Share / deep links land here: ?mode=multiplayer&join=<code>. Open the
  // Multiplayer tab and pass the join code down to the royale panel.
  const routeMode: string | undefined = route?.params?.mode;
  const royaleJoinCode: string | undefined = route?.params?.join;
  // Coach opened this tab from the classroom to auto-create a royale.
  const royaleAutoCreateKey: string | undefined =
    route?.params?.royaleAutoCreate;
  // Student tapped "Join Puzzle Royale" — join automatically, no second click.
  const royaleAutoJoin: boolean =
    route?.params?.autoJoin === '1' || route?.params?.autoJoin === true;
  // Coach reopening a classroom royale — carry context so a rematch from this
  // tab re-registers with Django (notify students).
  const royaleClassroomMeeting: string | undefined =
    route?.params?.crMeeting;
  const royaleClassroomOccurence: string | undefined =
    route?.params?.crOccurence;
  // Dashboard "Fun Challenge" tile deep-link: ?mode=solo&autoStart=1 — land on
  // Solo AND skip its "Start game" tap too (see SoloPuzzleRoyalePanel).
  const soloAutoStart: boolean =
    route?.params?.autoStart === '1' || route?.params?.autoStart === true;
  // Dashboard "Puzzle Royale" tile picks a race time: ?time=<seconds>, validated
  // against the royale chips so a mangled link falls back to the default.
  const soloTimeParam = Number(route?.params?.time);
  const soloTimerSec = (TIME_CHIPS as readonly number[]).includes(soloTimeParam)
    ? soloTimeParam
    : undefined;
  const {searchPlayers, searchPlayersResult, username} =
    useFunChallengesUserSearch();
  const {sendChallenge, ready} = useFunChallengesBattleSocket();

  const status = useFunChallengesBattleStore(s => s.status);
  const openSettingsOnLobby = useFunChallengesBattleStore(
    s => s.openSettingsOnLobby,
  );
  const clearOpenSettingsOnLobby = useFunChallengesBattleStore(
    s => s.clearOpenSettingsOnLobby,
  );

  // If the user already has an active royale lobby/session in the store (e.g.
  // they created a lobby, navigated away, and came back), drop them straight
  // back into it: Multiplayer tab + Puzzle Royale selected.
  const hasActiveRoyale = useFunChallengesRoyaleStore(
    s => s.status === 'lobby' || s.status === 'in_battle',
  );

  // Solo is the default tab; multiplayer deep-links / live sessions still win.
  const [mode, setMode] = useState<LobbyMode>(
    routeMode === 'multiplayer' ||
      royaleJoinCode ||
      royaleAutoCreateKey ||
      hasActiveRoyale
      ? 'multiplayer'
      : 'solo',
  );
  // Staff-only "Host" tab gating (D4) + the host's display name for create.
  const isStaff = useLoggedInUserStore(s => !!s.userData?.is_staff);
  const hostName = useLoggedInUserStore(
    s =>
      s.userData?.username ||
      s.userData?.name ||
      s.userData?.first_name ||
      '',
  );
  // Which multiplayer mode is picked. Puzzle Royale is the only mode today, so
  // it's selected BY DEFAULT (like Square Trainer in the Duel tab) — the user
  // doesn't have to click the tile first. The tile still shows as selected and
  // can be re-tapped; more modes later would make a null default meaningful.
  const [selectedMultiplayerType, setSelectedMultiplayerType] =
    useState<BattleType | null>(BattleType.PUZZLE_ROYALE);
  // Solo mode tile selection — Solo Puzzle Royale is the only solo mode today,
  // so it's selected by default (like Square Trainer / Puzzle Royale).
  const [selectedSoloType, setSelectedSoloType] = useState<BattleType | null>(
    BattleType.PUZZLE_ROYALE,
  );

  // Re-entry while a royale lobby/session is live: snap to Multiplayer +
  // Puzzle Royale (covers the case where the store became active AFTER mount).
  useEffect(() => {
    if (hasActiveRoyale) {
      setMode('multiplayer');
      setSelectedMultiplayerType(BattleType.PUZZLE_ROYALE);
    }
  }, [hasActiveRoyale]);
  const [selectedBattleType, setSelectedBattleType] = useState<BattleType>(
    BattleType.SQUARE_TRAINER,
  );
  const [opponent, setOpponent] = useState<{id: string; name: string} | null>(
    null,
  );
  const [settings, setSettings] = useState<Settings>(
    DEFAULT_CHALLENGE_SETTINGS,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Phase 6 hardening: when the user clicked "Rematch with different
  // settings" on the previous battle, the façade flagged this; auto-open
  // the settings SlideModal then clear the flag so a manual return doesn't
  // re-open it.
  useEffect(() => {
    if (openSettingsOnLobby) {
      setSettingsOpen(true);
      clearOpenSettingsOnLobby();
    }
  }, [openSettingsOnLobby, clearOpenSettingsOnLobby]);

  // Client-side busy guards. Server enforces these too (see
  // funChallengeRoomProcessor.js::checkPlayerNotBusy), so this is pure UX:
  // the button stays disabled instead of letting the user fire a doomed
  // FUN_ROOM_CREATE and bounce off SERVER_BUSY.
  //   - playing: I'm in a chess multiplayer game (set by gameRoomProcessor).
  //   - joinedTournament: I've registered for a tournament — server-side
  //     guard (joinLeaveProcessor:136-192 / funChallengeRoomProcessor's
  //     checkPlayerNotBusy) decides whether the start window has crossed
  //     the threshold; we just surface a hint.
  const playingChess = useGamePlayState(s => s.playing);
  const joinedTournament = useGamePlayState(s => s.joinedTournament);
  const isBusy = playingChess || joinedTournament;

  const canSendChallenge =
    !!opponent && !!opponent.id && ready && status === 'idle' && !isBusy;

  const handleOpponentName = (raw: string | undefined) => {
    if (!raw) {
      setOpponent(null);
      return;
    }
    // ChallengeUserComponent emits `${userId}:${username}` strings.
    const idx = raw.indexOf(':');
    const id = idx > -1 ? raw.slice(0, idx) : raw;
    const name = idx > -1 ? raw.slice(idx + 1) : '';
    if (id && id !== username) {
      setOpponent({id, name});
    }
  };

  const handleSend = () => {
    if (!opponent) return;
    sendChallenge({
      opponentId: opponent.id,
      opponentName: opponent.name,
      battleType: selectedBattleType,
      settings,
    });
  };

  const orientationLabel =
    settings.orientation === 'black' ? 'As Black' : 'As White';
  const isPTB = selectedBattleType === BattleType.POSITION_TRAINER;
  const isPDB = selectedBattleType === BattleType.PUZZLE_DASH;

  // Swap to the matching defaults when the user flips the tile selection.
  // Done in an effect so a manual edit isn't clobbered. Heuristic:
  //   ST  → no maxWrong, no targetCorrect
  //   PTB → maxWrong yes, targetCorrect no
  //   PDB → targetCorrect yes (always implies maxWrong)
  // We only reset when the current settings clearly belong to a different
  // mode than the newly-selected one.
  useEffect(() => {
    const hasMaxWrong = settings.maxWrong !== undefined;
    const hasTarget = settings.targetCorrect !== undefined;
    if (isPDB && !hasTarget) {
      setSettings(DEFAULT_PDB_CHALLENGE_SETTINGS);
    } else if (isPTB && (!hasMaxWrong || hasTarget)) {
      setSettings(DEFAULT_PTB_CHALLENGE_SETTINGS);
    } else if (!isPTB && !isPDB && (hasMaxWrong || hasTarget)) {
      setSettings(DEFAULT_CHALLENGE_SETTINGS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPTB, isPDB]);

  const ctaLabel = !ready
    ? 'Connecting…'
    : playingChess
    ? 'You’re in a chess game'
    : joinedTournament
    ? 'Tournament starting soon'
    : !opponent
    ? 'Pick an opponent'
    : status === 'challenging'
    ? 'Waiting for opponent…'
    : 'Send Challenge';

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.scrollContent}>
      <View style={styles.headerBlock}>
        <View style={styles.headerAccent} />
        <CCText style={styles.heading}>Fun Challenges</CCText>
        <CCText style={styles.subheading}>
          {mode === 'duel'
            ? 'Quick 1-vs-1 brain games. Pick a challenge, search a friend, send the invite.'
            : mode === 'solo'
            ? 'Play on your own. Race the clock through a ladder of ever-harder puzzles.'
            : 'Big multiplayer races. Create a lobby, invite a crowd, and climb the live leaderboard.'}
        </CCText>
      </View>

      {/* Duel / Multiplayer segmented control */}
      <ModeTabs mode={mode} onChange={setMode} isStaff={isStaff} />

      {mode === 'host' ? (
        <HostTournamentPanel creatorName={hostName} />
      ) : mode === 'solo' ? (
        <>
          <Section step="01" title="Choose a mode">
            <View style={styles.tileRow}>
              {SOLO_TILE_SPECS.map(t => (
                <ChallengeTile
                  key={t.battleType}
                  spec={t}
                  selected={selectedSoloType === t.battleType}
                  onPress={() => setSelectedSoloType(t.battleType)}
                />
              ))}
            </View>
          </Section>

          {selectedSoloType === BattleType.PUZZLE_ROYALE && (
            <Section step="02" title="Set up your game">
              <SoloPuzzleRoyalePanel
                autoStart={soloAutoStart}
                initialTimerSec={soloTimerSec}
              />
            </Section>
          )}
        </>
      ) : mode === 'multiplayer' ? (
        <>
          <Section step="01" title="Choose a mode">
            <View style={styles.tileRow}>
              {MULTIPLAYER_TILE_SPECS.map(t => (
                <ChallengeTile
                  key={t.battleType}
                  spec={t}
                  selected={selectedMultiplayerType === t.battleType}
                  onPress={() => setSelectedMultiplayerType(t.battleType)}
                />
              ))}
            </View>
          </Section>

          {/* Options appear only after a multiplayer mode is picked. Puzzle
              Royale's full create/join/spectate + waiting-room panel renders
              inline — no separate route, no opponent picker. `royaleJoinCode`
              comes from a share/deep link (?mode=multiplayer&join=). */}
          {selectedMultiplayerType === BattleType.PUZZLE_ROYALE && (
            <Section step="02" title="Set up the race">
              <PuzzleRoyaleLobbyPanel
                initialJoinCode={royaleJoinCode}
                autoJoin={royaleAutoJoin}
                classroomAutoCreateKey={royaleAutoCreateKey}
                classroomMeetingId={royaleClassroomMeeting}
                classroomOccurenceId={royaleClassroomOccurence}
              />
            </Section>
          )}
        </>
      ) : (
        <>
          <Section step="01" title="Choose a challenge">
            <View style={styles.tileRow}>
              {DUEL_TILE_SPECS.map(t => (
                <ChallengeTile
                  key={t.battleType}
                  spec={t}
                  selected={selectedBattleType === t.battleType}
                  onPress={() => setSelectedBattleType(t.battleType)}
                />
              ))}
            </View>
          </Section>

          {/* zIndex / elevation lift the opponent picker above siblings so the
          react-select dropdown menu (rendered inline by ChallengeUserComponent)
          isn't hidden by the Settings card or Send button below it. */}
          <View style={styles.opponentSection}>
            <Section step="02" title="Choose an opponent">
              <Box style={styles.opponentCard}>
                <ChallengeUserComponent
                  handleOpponentName={handleOpponentName}
                  searchPlayers={searchPlayers}
                  searchPlayersResult={searchPlayersResult}
                  user={username}
                />
              </Box>
            </Section>
          </View>

          <Section step="03" title="Settings">
        <Pressable
          onPress={() => setSettingsOpen(true)}
          style={({pressed}) => [
            styles.settingsCard,
            pressed && styles.settingsCardPressed,
          ]}>
          <View style={styles.settingsChips}>
            {isPDB ? (
              <>
                <IconChip
                  icon="flag-checkered"
                  text={`Race to ${settings.targetCorrect ?? 15}`}
                />
                <IconChip
                  icon="close-circle-outline"
                  text={
                    (settings.maxWrong ?? 5) >= 100
                      ? 'Unlimited mistakes'
                      : `${settings.maxWrong ?? 5} wrong`
                  }
                />
              </>
            ) : (
              <>
                <IconChip icon="timer-outline" text={`${settings.timerSec}s`} />
                {isPTB ? (
                  <IconChip
                    icon="close-circle-outline"
                    text={`${settings.maxWrong ?? 5} wrong`}
                  />
                ) : (
                  <IconChip icon="compass-outline" text={orientationLabel} />
                )}
              </>
            )}
          </View>
          <View style={styles.settingsHintWrap}>
            <CCText style={styles.settingsHint}>Tap to edit</CCText>
            <CCText style={styles.settingsHintArrow}>›</CCText>
          </View>
        </Pressable>
      </Section>

          <Pressable
            onPress={canSendChallenge ? handleSend : undefined}
            disabled={!canSendChallenge}
            style={({pressed}) => [
              styles.sendBtn,
              !canSendChallenge && styles.sendBtnDisabled,
              canSendChallenge && pressed && styles.sendBtnPressed,
            ]}>
            <CCText
              style={[
                styles.sendBtnText,
                !canSendChallenge && styles.sendBtnTextDisabled,
              ]}>
              {ctaLabel}
            </CCText>
          </Pressable>

          <SlideModal
            visible={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            modalHeaderTitle="Challenge settings"
            type="center"
            contentPadding={20}>
            <ChallengeSettings
              value={settings}
              onChange={setSettings}
              battleType={selectedBattleType}
            />
            <CCButton
              type="fill"
              stretched
              onPress={() => setSettingsOpen(false)}
              style={{marginTop: 16}}>
              Submit
            </CCButton>
          </SlideModal>
        </>
      )}
    </ScrollView>
  );
}

/** A settings-summary pill with a leading MaterialCommunityIcons glyph. */
function IconChip({icon, text}: {icon: string; text: string}) {
  return (
    <View style={styles.chip}>
      <MaterialCommunityIcons
        name={icon}
        size={13}
        color={TEAL_DARK}
        style={styles.chipIcon}
      />
      <CCText style={styles.chipText}>{text}</CCText>
    </View>
  );
}

/** Duel / Multiplayer segmented control (brown-pill, like Visualization Trainer). */
function ModeTabs({
  mode,
  onChange,
  isStaff,
}: {
  mode: LobbyMode;
  onChange: (m: LobbyMode) => void;
  isStaff?: boolean;
}) {
  const tabs: Array<{key: LobbyMode; label: string}> = [
    {key: 'solo', label: 'Solo'},
    {key: 'duel', label: 'Duel'},
    {key: 'multiplayer', label: 'Multiplayer'},
    // Host is staff-only (D4) — the create/cancel write path is front-end gated
    // (server accepts the socket msg; UI is the gate).
    ...(isStaff ? [{key: 'host' as LobbyMode, label: 'Host'}] : []),
  ];
  return (
    <View style={styles.modeTabs}>
      {tabs.map(t => {
        const active = mode === t.key;
        return (
          <Pressable
            key={t.key}
            onPress={() => onChange(t.key)}
            style={[styles.modeTab, active && styles.modeTabActive]}>
            <CCText
              style={[styles.modeTabText, active && styles.modeTabTextActive]}>
              {t.label}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );
}

function Section({
  title,
  step,
  children,
}: {
  title: string;
  step?: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        {step ? (
          <View style={styles.sectionStep}>
            <CCText style={styles.sectionStepText}>{step}</CCText>
          </View>
        ) : null}
        <CCText style={styles.sectionTitle}>{title}</CCText>
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {flex: 1, backgroundColor: BG_PAGE},
  scrollContent: {
    padding: 28,
    paddingBottom: 64,
    gap: 20,
    maxWidth: 1180,
    width: '100%',
    alignSelf: 'center',
  },
  headerBlock: {
    marginBottom: vs(4),
  },
  headerAccent: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: YELLOW,
    marginBottom: 14,
  },
  heading: {
    fontSize: FontSizes[32] || 32,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: -0.5,
    lineHeight: 38,
  },
  subheading: {
    fontSize: FontSizes[14] || 14,
    color: TEXT_MID,
    marginTop: vs(6),
    lineHeight: 20,
  },
  // Duel / Multiplayer segmented control (brown active pill on a white track).
  modeTabs: {
    flexDirection: 'row',
    backgroundColor: CCColors.White,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: BORDER_STRONG,
    borderRadius: 10,
    padding: 0,
    overflow: 'hidden',
    alignSelf: 'flex-start',
    marginTop: vs(8),
    maxWidth: 480,
    width: '100%',
  },
  modeTab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modeTabActive: {
    backgroundColor: TEXT_MID,
    borderRadius: 8,
  },
  modeTabText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '700',
    color: TEXT_MID,
  },
  modeTabTextActive: {
    color: CCColors.White,
  },
  section: {marginTop: vs(10)},
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: vs(12),
  },
  sectionStep: {
    minWidth: 28,
    height: 22,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: BG_PAGE_SOFT,
    borderWidth: 1,
    borderColor: BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionStepText: {
    fontSize: FontSizes[11] || 11,
    fontWeight: '800',
    color: TEXT_MID,
    letterSpacing: 0.4,
  },
  sectionTitle: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MID,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  tileRow: {flexDirection: 'row', gap: 14, flexWrap: 'wrap'},
  tile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    minWidth: 320,
    maxWidth: 480,
    flex: 1,
    padding: 20,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: {width: 0, height: 4},
    position: 'relative',
    overflow: 'hidden',
  },
  tilePressed: {
    borderColor: BORDER_STRONG,
    transform: [{translateY: 1}],
  },
  tileSelected: {
    borderColor: TEAL,
    borderWidth: 2,
    backgroundColor: TEAL_TINT_SOFT,
    shadowColor: TEAL,
    shadowOpacity: 0.18,
    shadowRadius: 14,
    shadowOffset: {width: 0, height: 6},
  },
  tileAccentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: TEAL,
  },
  tileIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileIcon: {width: 40, height: 42},
  tileBody: {flex: 1},
  tileTitle: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '800',
    color: TEXT_DARK,
    marginBottom: 4,
    letterSpacing: -0.2,
  },
  tileDesc: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MID,
    lineHeight: 18,
  },
  tileCheck: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: TEAL,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: TEAL,
    shadowOpacity: 0.35,
    shadowRadius: 6,
    shadowOffset: {width: 0, height: 2},
  },
  tileCheckMark: {
    color: CCColors.White,
    fontWeight: '800',
    fontSize: 13,
    lineHeight: 14,
  },
  opponentSection: {
    zIndex: 100,
    elevation: 100,
    position: 'relative',
  },
  opponentCard: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  settingsCard: {
    padding: 18,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  settingsCardPressed: {
    borderColor: BORDER_STRONG,
    backgroundColor: BG_PAGE_SOFT,
  },
  settingsChips: {flexDirection: 'row', gap: 8, flexWrap: 'wrap', flex: 1},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: TEAL_TINT,
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.30)',
  },
  chipIcon: {marginTop: 0.5},
  chipText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEAL_DARK,
    letterSpacing: 0.1,
  },
  settingsHintWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  settingsHint: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    fontWeight: '600',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  settingsHintArrow: {
    fontSize: 18,
    color: TEXT_MUTED,
    fontWeight: '700',
    lineHeight: 18,
    marginTop: -2,
  },
  sendBtn: {
    marginTop: vs(10),
    backgroundColor: YELLOW,
    borderRadius: 14,
    borderBottomWidth: 4,
    borderColor: YELLOW_DARK,
    paddingVertical: 18,
    paddingHorizontal: 32,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: YELLOW_DARK,
    shadowOpacity: 0.18,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 4},
  },
  sendBtnPressed: {
    borderBottomWidth: 2,
    paddingVertical: 20,
  },
  sendBtnDisabled: {
    backgroundColor: '#F2E8C7',
    borderColor: '#D9C796',
    shadowOpacity: 0,
  },
  sendBtnText: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  sendBtnTextDisabled: {
    color: '#8A7E5F',
  },
});
